import React, { useState } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, Avatar, Button, Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as ApprovedIcon,
  Warning as ChangesIcon,
  Refresh as RevalidationIcon,
  PersonAdd as AssignedIcon,
  NotificationsNone as EmptyIcon
} from '@mui/icons-material';
import useNotifications from '../../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';

const typeConfig = {
  validation_submitted: { icon: ApprovedIcon, color: '#2e7d32', bgColor: '#e8f5e9', label: 'Approved' },
  changes_requested: { icon: ChangesIcon, color: '#d32f2f', bgColor: '#ffebee', label: 'Changes requested' },
  revalidation_needed: { icon: RevalidationIcon, color: '#f57f17', bgColor: '#fff8e1', label: 'Re-review needed' },
  validator_assigned: { icon: AssignedIcon, color: '#1976d2', bgColor: '#e3f2fd', label: 'Assigned' },
};

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    notifications, unreadCount, loading,
    fetchNotifications, markAsRead, markAllAsRead
  } = useNotifications();
  const navigate = useNavigate();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    handleClose();
    if (notification.project_id && notification.task_id) {
      navigate(`/projects/${notification.project_id}/conversation?task=${notification.task_id}`);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: 'white',
          '&:hover': { bgcolor: 'transparent' }
        }}
        title="Notifications"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #e5e5e5', bgcolor: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{
              fontWeight: 700, fontSize: 16,
              fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
              color: '#475969'
            }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                sx={{
                  height: 20, fontSize: 11, fontWeight: 600,
                  bgcolor: '#d32f2f', color: '#fff',
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllAsRead}
              sx={{ textTransform: 'none', fontSize: 12, color: '#1976d2', fontWeight: 500 }}
            >
              Mark all as read
            </Button>
          )}
        </Box>

        {/* Notification List */}
        <Box sx={{ overflow: 'auto', maxHeight: 420 }}>
          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
              <EmptyIcon sx={{ fontSize: 48, color: '#d0d5dd', mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: '#72849a', fontWeight: 500 }}>
                No notifications yet
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#a0aec0', mt: 0.5 }}>
                You'll be notified about validation updates here
              </Typography>
            </Box>
          ) : (
            notifications.map((notif) => {
              const config = typeConfig[notif.notification_type] || typeConfig.validation_submitted;
              const IconComp = config.icon;
              const triggeredByName = notif.triggered_by?.name || '';
              const initial = triggeredByName ? triggeredByName.charAt(0).toUpperCase() : '?';

              return (
                <Box
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    display: 'flex', gap: 1.5,
                    px: 2.5, py: 1.5,
                    cursor: 'pointer',
                    bgcolor: notif.is_read ? '#fff' : '#f8faff',
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: notif.is_read ? '3px solid transparent' : `3px solid ${config.color}`,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: notif.is_read ? '#f9f9f9' : '#f0f5ff' }
                  }}
                >
                  {/* Avatar with type icon */}
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar sx={{
                      width: 36, height: 36, fontSize: 14, fontWeight: 600,
                      bgcolor: config.bgColor, color: config.color
                    }}>
                      {initial}
                    </Avatar>
                    <Box sx={{
                      position: 'absolute', bottom: -2, right: -2,
                      width: 16, height: 16, borderRadius: '50%',
                      bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                    }}>
                      <IconComp sx={{ fontSize: 10, color: config.color }} />
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 13, lineHeight: 1.4, color: '#475969',
                      fontWeight: notif.is_read ? 400 : 500,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {notif.message}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: 11, color: '#a0aec0' }}>
                        {getTimeAgo(notif.created_at)}
                      </Typography>
                      {notif.task_id && (
                        <Chip
                          label={notif.task_id.replace(/-/g, ' ')}
                          size="small"
                          sx={{
                            height: 18, fontSize: 10,
                            bgcolor: '#f5f5f5', color: '#72849a',
                            '& .MuiChip-label': { px: 0.75 }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Unread dot */}
                  {!notif.is_read && (
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: config.color, mt: 0.5, flexShrink: 0
                    }} />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
